import { buildAddFileFormData, mapToSubmitClients } from '@/company-portal/add-file/add-file-utils';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileSubmitRequest, AddFileValidateRequest } from '@/company-portal/add-file/add-file.types';
import { csrfToken } from '@/components/Utils/Helper';
import { calendarDateToNativeDate } from '@/shared/utils/calendar-date-utils';
import { formatDate, MONTH_DAY_YEAR_FORMAT } from '@/shared/utils/format-date';
import { formatCentsAsMoney } from '@/shared/utils/format-number';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { computed } from 'vue';

/**
 * request payload builder for legacy form submission, POST user/home
 * @todo [CLENG-4993] cleanup when new_add_file_submission_enabled FF is decommissioned
 */
export const useAddFilePayload = () => {
    const addFileStore = useAddFileStore();
    const fileClientStore = useFileClientStore();
    const fileDocumentStore = useFileDocumentStore();

    const buildValidateRequest = (): AddFileValidateRequest => ({
        token: csrfToken.value,
        refNumber: addFileStore.refNumber,
        address: addFileStore.address,
        companyId: addFileStore.company?.id,
        branchId: addFileStore.branch?.id,
        closeDate: addFileStore.closeDate
            ? formatDate(calendarDateToNativeDate(addFileStore.closeDate), MONTH_DAY_YEAR_FORMAT)
            : '',
        balanceDue: addFileStore.balanceDue ? formatCentsAsMoney(addFileStore.balanceDue) : undefined,
        notes: addFileStore.notes,
        wireDocId: addFileStore.wireDoc?.id,
        notify: addFileStore.notifyClients,
        clients: mapToSubmitClients({
            availableClientOptions: fileClientStore.startedClientOptions,
            documentConfigs: fileDocumentStore.selectedDocumentConfigs,
            requestWireInfoClientIds: addFileStore.requestWireInfoClientIds,
            requestIdvClientIds: addFileStore.requestIdvClientIds,
            wireDocRecipientIds: addFileStore.wireDocRecipientClientIds,
            // Keep tasked clients (the payload) plus clients without a role (so the role guard can
            // flag them). On a valid submit no items without a clients remain, so the payload is unchanged.
        }).filter(client => client.included || !client.role),
        wireDocRecipientIds: addFileStore.wireDocRecipientClientIds.map(String),
        requestBankInfoRecipientIds: addFileStore.requestWireInfoClientIds.map(String),
        requestIdvRecipientIds: addFileStore.requestIdvClientIds.map(String),
        notificationUserIds: addFileStore.notificationUsers.map(user => Number(user.value)),
        vendor: addFileStore.tpsOrder
            ? {
                  vendor: addFileStore.tpsOrder.vendor,
                  vendor_order_id: addFileStore.tpsOrder.vendor_order_id,
                  vendor_transaction_id: addFileStore.tpsOrder.vendor_transaction_id,
                  amount_down: addFileStore.tpsOrder.amount_down ?? '',
                  earnest_money: addFileStore.tpsOrder.earnest_money ?? '',
                  sales_price: addFileStore.tpsOrder.sales_price,
              }
            : undefined,
    });

    // Exposed so the client-role guard can validate the outgoing clients before any server call.
    const validateRequest = computed(() => buildValidateRequest());

    const buildValidateFormData = (): FormData => buildAddFileFormData(buildValidateRequest());

    const buildSubmitRequest = (): AddFileSubmitRequest => ({
        ...buildValidateRequest(),
        documents: fileDocumentStore.getFormUploadFormDataV1(),
    });

    const buildSubmitFormData = (): FormData => buildAddFileFormData(buildSubmitRequest());

    return {
        validateRequest,
        buildValidateRequest,
        buildValidateFormData,
        buildSubmitRequest,
        buildSubmitFormData,
    };
};
